import classnames from 'classnames'
import React, { useId, useRef } from 'react'

import Field from '../Field/Field'
import FieldDropdown from '../FieldDropdown/FieldDropdown'
import FieldError from '../FieldError/FieldError'

import { useCombobox, UseComboboxProps } from './useCombobox'

import styles from './Combobox.module.scss'

type ComboboxProps = {
  name?: string
  label?: string
  placeholder?: string
  value?: string

  touched?: boolean
  error?: string
  omitErrorDisplay?: boolean
} & UseComboboxProps

export default React.memo(function Combobox({
  name,
  label,
  placeholder,
  value,
  touched,
  error,
  omitErrorDisplay,
  ...comboboxProps
}: ComboboxProps) {
  const listRef = useRef<HTMLDivElement | null>(null)
  const fieldRef = useRef<HTMLDivElement | null>(null)

  const dropdownId = useId()

  const { open, activeOption, activeOptionId, inputProps, listProps, getOptionProps } = useCombobox(comboboxProps)

  return (
    <div className={classnames(styles.Combobox)}>
      <Field
        ref={fieldRef}
        label={label}
        hasError={touched && Boolean(error)}
        className={classnames({ [styles.fieldInactive]: activeOption })}
        disabled={inputProps.disabled}
      >
        <input
          name={name}
          placeholder={placeholder}
          className={styles.input}
          size={1}
          aria-label={label}
          value={value}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={open ? dropdownId : undefined}
          aria-expanded={open}
          aria-activedescendant={activeOptionId}
          {...inputProps}
        />
      </Field>
      <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} />
      <FieldDropdown ref={listRef} id={dropdownId} open={open} anchor={fieldRef.current} {...listProps}>
        {comboboxProps.options.map(({ label, value, selected, disabled }, idx) => (
          <div
            key={idx}
            role="option"
            aria-checked={selected}
            aria-current={idx === activeOption?.idx}
            aria-disabled={disabled || undefined}
            {...getOptionProps({ value, idx })}
          >
            {label}
          </div>
        ))}
      </FieldDropdown>
    </div>
  )
})
